import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { CustomerWS, AddressWS, StateWS, UpdateCustomerPayload, CreateAddressPayload, UpdateAddressPayload } from '@/types/prestashop';

// Hook para obtener cliente por email
export const useCustomer = (email: string) => {
  return useQuery({
    queryKey: ['customer', email],
    queryFn: async (): Promise<CustomerWS> => {
      const response = await fetch(`/api/es/customer?email=${encodeURIComponent(email)}`);
      if (!response.ok) {
        throw new Error('Error obteniendo cliente');
      }
      const data = await response.json();
      return data.customer;
    },
    enabled: !!email,
  });
};

// Hook para actualizar cliente
export const useUpdateCustomer = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (customerData: UpdateCustomerPayload): Promise<CustomerWS> => {
      const response = await fetch('/api/es/customer', {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(customerData),
      });
      
      if (!response.ok) {
        throw new Error('Error actualizando cliente');
      }
      
      const data = await response.json();
      return data.customer;
    },
    onSuccess: (updatedCustomer) => {
      // Invalidar y actualizar la caché del cliente
      queryClient.invalidateQueries({ queryKey: ['customer', updatedCustomer.email] });
      queryClient.setQueryData(['customer', updatedCustomer.email], updatedCustomer);
    },
  });
};

// Hook para obtener direcciones de un cliente
export const useCustomerAddresses = (customerId: number) => {
  const query = useQuery({
    queryKey: ['customer-addresses', customerId],
    queryFn: async (): Promise<AddressWS[]> => {
      const response = await fetch(`/api/es/customer/addresses?customerId=${customerId}`);
      if (!response.ok) {
        throw new Error('Error obteniendo direcciones');
      }
      const data = await response.json();
      return data.addresses;
    },
    enabled: !!customerId,
    refetchOnWindowFocus: false,
    refetchOnMount: true,
    staleTime: 0, // Los datos se consideran stale inmediatamente
    gcTime: 5 * 60 * 1000, // 5 minutos
  });

  return {
    ...query,
    refetch: query.refetch,
  };
};

// Hook para crear dirección
export const useCreateAddress = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (addressData: CreateAddressPayload): Promise<AddressWS> => {
      const response = await fetch('/api/es/customer/addresses/create', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(addressData),
      });
      
      if (!response.ok) {
        throw new Error('Error creando dirección');
      }
      
      const data = await response.json();
      return data.address;
    },
    onSuccess: (newAddress) => {
      // Invalidar la caché de direcciones del cliente
      queryClient.invalidateQueries({ 
        queryKey: ['customer-addresses', newAddress.id_customer],
        exact: true,
        refetchType: 'all'
      });
      
      // También invalidar cualquier query relacionada con direcciones
      queryClient.invalidateQueries({ 
        queryKey: ['customer-addresses'],
        refetchType: 'all'
      });
    },
  });
};

// Hook para actualizar dirección
export const useUpdateAddress = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (addressData: UpdateAddressPayload): Promise<AddressWS> => {
      const response = await fetch('/api/es/customer/addresses/update', {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(addressData),
      });
      
      if (!response.ok) {
        throw new Error('Error actualizando dirección');
      }
      
      const data = await response.json();
      return data.address;
    },
    onSuccess: (updatedAddress) => {
      // Invalidar la caché de direcciones del cliente
      queryClient.invalidateQueries({ 
        queryKey: ['customer-addresses', updatedAddress.id_customer],
        exact: true,
        refetchType: 'all'
      });
      
      // También invalidar cualquier query relacionada con direcciones
      queryClient.invalidateQueries({ 
        queryKey: ['customer-addresses'],
        refetchType: 'all'
      });
    },
  });
};

// Hook para obtener estados por país
export const useStates = (countryId: number) => {
  return useQuery({
    queryKey: ['states', countryId],
    queryFn: async (): Promise<StateWS[]> => {
      const response = await fetch(`/api/es/states?countryId=${countryId}`);
      if (!response.ok) {
        throw new Error('Error obteniendo estados');
      }
      const data = await response.json();
      return data.states;
    },
    enabled: !!countryId,
  });
};
